import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import tenantInvitationService, {
  type TenantInvitationPreview,
} from '../services/tenantInvitationService';
import { getApiErrorInfo } from '../services/api';
import { useUserContext } from '../hooks/useUserContext';
import { config } from '../config';

const DEFAULT_TENANT_ID = config.defaultTenantId;

export default function PendingInviteBanner() {
  const { isLoaded, isSignedIn, refreshUser, backendProfile } = useUserContext();
  const isOnDefaultTenant = !backendProfile?.tenantId || backendProfile.tenantId === DEFAULT_TENANT_ID;
  const tenantId = backendProfile?.tenantId ?? DEFAULT_TENANT_ID;

  const [invitations, setInvitations] = useState<TenantInvitationPreview[]>([]);
  const [current, setCurrent] = useState<TenantInvitationPreview | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;

    tenantInvitationService
      .listMyPending(tenantId)
      .then((list) => {
        setInvitations(list);
        if (list.length > 0) setCurrent(list[0]);
      })
      .catch(() => {
        // Silently ignore — don't disrupt the app if this fails
      });
  }, [isLoaded, isSignedIn, tenantId]);

  const handleAccept = async () => {
    if (!current) return;
    try {
      setAccepting(true);
      setError(null);
      await tenantInvitationService.acceptById(current.invitationId, tenantId);
      await refreshUser();
      advance();
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Failed to accept invitation.');
    } finally {
      setAccepting(false);
    }
  };

  const advance = () => {
    const remaining = invitations.filter((i) => i.invitationId !== current?.invitationId);
    setInvitations(remaining);
    setCurrent(remaining[0] ?? null);
    setError(null);
  };

  if (!current) return null;

  return (
    <Dialog open maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle sx={{ fontWeight: 700 }}>Organization invitation</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography sx={{ mb: 2 }}>
          <strong>{current.invitedByName || 'An administrator'}</strong> has invited you to join{' '}
          <strong>{current.tenantName}</strong> as a <strong>{current.role}</strong>.
        </Typography>

        {isOnDefaultTenant ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Accepting will move your account to <strong>{current.tenantName}</strong>.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            Accepting will move your account from <strong>{current.currentTenantName}</strong> to{' '}
            <strong>{current.tenantName}</strong>. You'll lose access to anything only available in
            your current organization.
          </Alert>
        )}

        <Typography variant="caption" color="text.secondary">
          Expires {new Date(current.expiresAt).toLocaleDateString()}.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Stack direction="row" spacing={1} width="100%" justifyContent="flex-end">
          <Button variant="outlined" onClick={advance} disabled={accepting}>
            Not now
          </Button>
          <Button
            variant="contained"
            onClick={handleAccept}
            disabled={accepting}
            startIcon={accepting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {accepting ? 'Accepting…' : `Join ${current.tenantName}`}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
